/**
 * Shared Bootstrap — Unified Application Entry Logic
 *
 * Responsibilities:
 *   - Resolve the active template via templateResolver
 *   - Execute startup initialization (PaceProgress, Session auth, UserInfo)
 *   - Compose top-level providers (Redux, Translation, BrowserRouter)
 *   - Build and mount the final route tree via routing/index.js
 *   - Render AppShell as the persistent top-level wrapper
 *
 * This function must stay clean. Template-specific startup hooks should be
 * expressed through templateConfig.onBootstrap (future extension point),
 * not by branching directly inside this file.
 */

import 'core-js/stable';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Provider } from 'react-redux';
import 'moment-timezone';

import {
    store,
    config,
    TranslationProvider,
    LegacyTranslationProvider,
    GlobalLoading,
    SessionActions,
    PaceProgressActions,
    UserInfoActions,
} from '../adapters/bootstrapAdapters';

import {
    RouterSync,
    NavigationBinder,
} from '../adapters/shellAdapters';

import {
    NotFoundPage,
    ServerErrorPage,
    AuthorisationErrorPage,
} from '../adapters/routeAdapters';

import AppShell from '../app/App';
import { buildRoutes } from '../routing/index';
import { resolveTemplate } from './templateResolver';
import { TemplateConfigProvider } from '../core/context/TemplateConfigContext';

export function startApp() {
    const skin = process.env.SKIN || 'cpa';
    const templateConfig = resolveTemplate(skin);

    PaceProgressActions.init();

    const routes = buildRoutes();
    const appElem = document.getElementById('unified-app');
    const root = ReactDOM.createRoot(appElem);

    SessionActions.authorize(() => {
        UserInfoActions.loadInfo().then(() => {
            root.render(
            <TemplateConfigProvider config={templateConfig}>
                <Provider store={store}>
                    <TranslationProvider>
                        <LegacyTranslationProvider>
                            <BrowserRouter basename={config.defaultRoute || '/'}>
                                <RouterSync>
                                    <NavigationBinder>
                                        <Routes>
                                            <Route path={'/'} element={<AppShell />}>
                                                {routes.map((item, i) => (
                                                    i === 0 || item.index
                                                        ? <Route key={item.key} index element={<item.component />} />
                                                        : <Route key={item.key} path={item.path} element={<item.component />} />
                                                ))}
                                            </Route>
                                            <Route path={'/500'} element={<ServerErrorPage />} />
                                            <Route path={'/401'} element={<AuthorisationErrorPage />} />
                                            <Route path="*" element={<NotFoundPage />} />
                                        </Routes>
                                    </NavigationBinder>
                                </RouterSync>
                            </BrowserRouter>
                        </LegacyTranslationProvider>
                    </TranslationProvider>
                </Provider>
            </TemplateConfigProvider>
        );
        });
    }, () => {
        root.render(<GlobalLoading />);
    });
}
